import React, { useState, useEffect } from 'react';
import { LuCircleCheck, LuClock, LuCreditCard, LuExternalLink, LuReceiptText, LuRefreshCw, LuUpload } from 'react-icons/lu';
import useFeeStore from '../../store/feeStore';
import useSchoolStore from '../../store/schoolStore';
import useUserStore from '../../store/userStore';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingMessage from '../../components/common/LoadingMessage';
import ErrorWidget from '../../components/common/ErrorWidget';
import PageHeader from '../../components/common/PageHeader';
import Tabs from '../../components/common/Tabs';
import Table, { tdClass } from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import EmptyState from '../../components/common/EmptyState';
import { useToast } from '../../components/common/Toast';
import FeeUploadWindow from './components/FeeUploadWindow';
import {getClassName} from '../../utils/schoolUtils';
import { PaymentStatus } from '../../models/online_payment';
import { openLink } from '../../utils/utils';

const FeePage = () => {
  const { showToast, ToastComponent } = useToast();
  const [activeTab, setActiveTab] = useState('receipts');
  const [showUploadWindow, setShowUploadWindow] = useState(false);
  const [uploadType, setUploadType] = useState('receipt');

  const {
    onlinePayments,
    feeReceipts,
    feeDues,
    isOnlinePaymentsLoading,
    isFeeReceiptsLoading,
    isFeeDuesLoading,
    onlinePaymentsError,
    feeReceiptsError,
    feeDuesError,
    getOnlinePayments,
    getFeeReceipts,
    getFeeDues,
    refreshAllData,
    clearUploadState
  } = useFeeStore();

  const { schoolData } = useSchoolStore();
  const { currentUser } = useUserStore();

  // Load once the school and the signed-in user are both known: useFeeStore needs both,
  // and after a page reload the user arrives a moment later (DashboardLayout.getCurrentUser)
  useEffect(() => {
    if (schoolData?.id && currentUser) {
      loadTabData(activeTab);
    }
  }, [schoolData?.id, currentUser, activeTab]);

  const loadTabData = async (tab) => {
    try {
      switch (tab) {
        case 'payments':
          if (!onlinePayments) {
            await getOnlinePayments();
          }
          break;
        case 'receipts':
          if (!feeReceipts) {
            await getFeeReceipts();
          }
          break;
        case 'dues':
          if (!feeDues) {
            await getFeeDues();
          }
          break;
      }
    } catch (error) {
      console.error('Error loading fee data:', error);
    }
  };


  const openInRazorPay = (payment)=>{
    const razorPayUrl = `https://dashboard.razorpay.com/app/payments/${payment.paymentId}?init_page=Payments`
    openLink(razorPayUrl);
  }
  
  const handleRefresh = async () => {
    try {
      await refreshAllData();
      showToast('Fee data refreshed successfully!', 'success');
    } catch (error) {
      showToast('Failed to refresh data. Please try again.', 'error');
    }
  };

  const handleUploadSuccess = (result) => {
      if(uploadType === 'receipt'){
        showToast('Fee receipts uploaded successfully!', 'success');
      }else{
        showToast('Fee dues uploaded successfully!', 'success');
      }
  };

  const handleReceiptUpload = () => {
    setUploadType('receipt');
    setShowUploadWindow(true);
  };

  const handleDueUpload = () => {
    setUploadType('due');
    setShowUploadWindow(true);
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };



  const renderOnlinePayments = () => {
    if (isOnlinePaymentsLoading) {
      return <LoadingMessage message="Loading online payments…" />;
    }

    if (onlinePaymentsError) {
      return <ErrorWidget error={onlinePaymentsError} onRetry={() => getOnlinePayments()} className="my-8 border-0 shadow-none" />;
    }

    if (!onlinePayments || onlinePayments.length === 0) {
      return <EmptyState icon={<LuCreditCard />} title="No online payments yet" message="Payments made by parents in the SchoolLine app will appear here." />;
    }

    // Status label and badge colour from the PaymentStatus constants
    const getStatusDisplay = (status) => {
      if (status === PaymentStatus.CAPTURED) {
        return { text: 'Success', tone: 'green' };
      }
      if (status === PaymentStatus.AUTHORIZED) {
        return { text: 'Authorised', tone: 'amber' };
      }
      if (status === PaymentStatus.FAILED) {
        return { text: 'Failed', tone: 'red' };
      }
      if (!status) {
        return { text: 'Pending', tone: 'grey' };
      }
      return { text: 'Unknown', tone: 'grey' };
    };

    return (
      <Table head={['Date and time', 'Student', 'Class', 'Amount', 'Payment ID', 'Status', 'Academic year']} rightAlign={[3]}>
        {onlinePayments.map((payment, index) => {
          const statusDisplay = getStatusDisplay(payment.paymentStatus);
          return (
            <tr key={payment.id || index}>
              <td className={`${tdClass} whitespace-nowrap text-ui-ink-2`}>{formatDate(payment.time)}</td>
              <td className={`${tdClass} font-medium`}>{payment.name || '—'}</td>
              <td className={tdClass}>{getClassName(schoolData.id, payment.Class)}</td>
              <td className={`${tdClass} text-right font-medium tabular-nums`}>{payment.getFormattedAmount()}</td>
              <td className={tdClass}>
                <button
                  type="button"
                  onClick={() => openInRazorPay(payment)}
                  title="Open this payment in Razorpay"
                  className="inline-flex items-center gap-1 font-mono text-[13px] text-ui-link hover:underline"
                >
                  {payment.paymentId || '—'}
                  <LuExternalLink aria-hidden className="size-3.5" />
                </button>
              </td>
              <td className={tdClass}>
                <Badge tone={statusDisplay.tone}>{statusDisplay.text}</Badge>
              </td>
              <td className={`${tdClass} text-ui-ink-2`}>{payment.academicYear || '—'}</td>
            </tr>
          );
        })}
      </Table>
    );
  };

  const renderFeeReceipts = () => {
    if (isFeeReceiptsLoading) {
      return <LoadingMessage message="Loading fee receipts…" />;
    }

    if (feeReceiptsError) {
      return <ErrorWidget error={feeReceiptsError} onRetry={() => getFeeReceipts()} className="my-8 border-0 shadow-none" />;
    }

    if (!feeReceipts || feeReceipts.length === 0) {
      return (
        <EmptyState
          icon={<LuReceiptText />}
          title="No fee receipts yet"
          message="No receipts are recorded for the current academic year. Use “Upload receipts” to add them from an Excel file."
        />
      );
    }

    return (
      <Table head={['Date', 'Admission no.', 'Total paid', 'Voucher no.']} rightAlign={[2]}>
        {feeReceipts.map((receipt, index) => (
          <tr key={receipt.id || index}>
            <td className={`${tdClass} whitespace-nowrap text-ui-ink-2`}>{receipt.getFormattedDate()}</td>
            <td className={`${tdClass} font-medium`}>{receipt.getStudentIdentifier()}</td>
            <td className={`${tdClass} text-right font-medium tabular-nums`}>{receipt.getFormattedTotalPaid()}</td>
            <td className={`${tdClass} font-mono text-[13px]`}>{receipt.voucherNo || '—'}</td>
          </tr>
        ))}
      </Table>
    );
  };

  const renderFeeDues = () => {
    if (isFeeDuesLoading) {
      return <LoadingMessage message="Loading fee dues…" />;
    }

    if (feeDuesError) {
      return <ErrorWidget error={feeDuesError} onRetry={() => getFeeDues()} className="my-8 border-0 shadow-none" />;
    }

    if (!feeDues || feeDues.length === 0) {
      return <EmptyState icon={<LuCircleCheck />} title="No pending dues" message="All fees have been paid. Nothing is due." />;
    }

    return (
      <Table head={['Due date', 'Admission no.', 'Amount', 'Month', 'Status']} rightAlign={[2]}>
        {feeDues.map((due, index) => {
          const isOverdue = due.isOverdue();
          return (
            <tr key={due.id || index}>
              <td className={`${tdClass} whitespace-nowrap text-ui-ink-2`}>{due.getFormattedDate()}</td>
              <td className={`${tdClass} font-medium`}>{due.admissionNo || '—'}</td>
              <td className={`${tdClass} text-right font-medium tabular-nums`}>{due.getFormattedAmount()}</td>
              <td className={tdClass}>{due.getFormattedMonth()}</td>
              <td className={tdClass}>
                <Badge tone={isOverdue ? 'red' : 'amber'}>
                  {isOverdue ? `Overdue by ${due.getDaysOverdue()} days` : 'Due'}
                </Badge>
              </td>
            </tr>
          );
        })}
      </Table>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'payments':
        return renderOnlinePayments();
      case 'receipts':
        return renderFeeReceipts();
      case 'dues':
        return renderFeeDues();
      default:
        return null;
    }
  };

  const tabs = [
    { id: 'receipts', label: 'Fee receipts', icon: LuReceiptText },
    { id: 'dues', label: 'Fee dues', icon: LuClock },
    { id: 'payments', label: 'Online payments', icon: LuCreditCard },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Fees"
        subtitle="Receipts, pending dues and online payments for this academic year."
        actions={
          <>
            <Button variant="secondary" onClick={handleRefresh} leftIcon={<LuRefreshCw className="size-4" />}>
              Refresh
            </Button>
            {(activeTab === 'receipts' || activeTab === 'dues') && (
              <Button
                onClick={activeTab === 'receipts' ? handleReceiptUpload : handleDueUpload}
                leftIcon={<LuUpload className="size-4" />}
              >
                Upload {activeTab === 'receipts' ? 'receipts' : 'dues'}
              </Button>
            )}
          </>
        }
      />

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} className="mb-4" />

      <Card noPadding>{renderTabContent()}</Card>

      <FeeUploadWindow
        isOpen={showUploadWindow}
        onClose={() => setShowUploadWindow(false)}
        onUploadSuccess={handleUploadSuccess}
        uploadType={uploadType}
      />

      {ToastComponent}
    </div>
  );
};

export default FeePage;
